import { uniq } from 'lodash';

import { QUESTIONS_SUPPORTING_SHOW_HIDE_ANSWERS } from 'data/featuresPerQuestion';
import { QUOTA_MODE } from 'data/quotaModes';
import { ActionVisibilityValueType } from 'utils/adl-services';
import {
  isMatrixBipolarOrConstantNode,
  isMatrixSingleOrMultipleNode,
} from 'utils/nodeTypes';

import questionnaireMessages from 'containers/QuestionnairePage/messages';
import { VISIBILITY_ITEM_TYPE } from './constants';
import messages from './messages';

export function canHaveConditionalDisplayLogic(
  isScreening = false,
  isInVanWestendorpGroup = null,
) {
  if (isScreening) {
    return false;
  }
  if (isInVanWestendorpGroup) {
    return false;
  }

  return true;
}

export function canShowHideAnswers({
  type,
  isScreening = false,
  inVanWestendorpGroup = false,
  valueType = ActionVisibilityValueType.Answer,
}) {
  if (isScreening || inVanWestendorpGroup) {
    return false;
  }

  if (valueType === ActionVisibilityValueType.Column) {
    return (
      isNodeSupportConditionalDisplayLogicOnAnswers(type) &&
      !canShowHideAnswerOnlyInRows({ type })
    );
  }

  return isNodeSupportConditionalDisplayLogicOnAnswers(type);
}

function canSetVisibility(
  list,
  answersWithLogicFromOutcomes,
  predicateFn = (item) => !(item.isEmptyAnswer || item.isCarriedForward),
) {
  const rawList = list.filter(predicateFn);

  const hasSetList = rawList.filter((item) =>
    answersWithLogicFromOutcomes.includes(item.answerRefId || item.refId),
  );

  return hasSetList.length === 0 || hasSetList.length < rawList.length - 1;
}

export function canAddLogicVisibilityStatement({
  nodeType,
  isScreening,
  answers = [],
  answersWithLogicFromOutcomes = [],
}) {
  if (!canShowHideAnswers({ type: nodeType, isScreening })) {
    return { isEnabled: false };
  }

  const canSet = canSetVisibility(
    answers,
    answersWithLogicFromOutcomes,
    (item) => !item.isEmptyAnswer,
  );

  return {
    isEnabled: true,
    canSet,
  };
}

export function canApplyLogicVisibility({
  nodeType,
  isScreening,
  answers = [],
  answersWithLogicFromOutcomes = [],
}) {
  if (!canShowHideAnswers({ type: nodeType, isScreening })) {
    return { isEnabled: false };
  }

  const canSet = canSetVisibility(answers, answersWithLogicFromOutcomes);

  return {
    isEnabled: true,
    canSet,
  };
}

export function hasAnswerDisplayLogic(answer, answersWithLogicFromOutcomes) {
  return answersWithLogicFromOutcomes.includes(
    answer.answerRefId || answer.refId,
  );
}

export function hasLogicVisibilityError({
  nodeType,
  isScreening,
  answers = [],
  carriedAnswers = [],
  answersWithLogicFromOutcomes = [],
}) {
  if (!canShowHideAnswers({ type: nodeType, isScreening })) {
    return false;
  }

  if (answers.length === 0 && carriedAnswers.length > 0) {
    return carriedAnswers.some((answer) =>
      hasAnswerDisplayLogic(answer, answersWithLogicFromOutcomes),
    );
  }

  if (answers.length === 0) {
    return false;
  }

  return answers.every((answer) =>
    hasAnswerDisplayLogic(answer, answersWithLogicFromOutcomes),
  );
}

export function isLimitOutOfBoundForVisibility({
  nodeType,
  isScreening,
  answers = [],
  answersWithLogicFromOutcomes = [],
  minLimit,
}) {
  if (!canShowHideAnswers({ type: nodeType, isScreening })) {
    return false;
  }

  const answersWithOutDisplayLogic = answers.filter(
    (answer) => !hasAnswerDisplayLogic(answer, answersWithLogicFromOutcomes),
  ).length;

  return (
    answersWithOutDisplayLogic > 0 && minLimit > answersWithOutDisplayLogic
  );
}

export function enrichAnswersWithVisibility({
  nodeType,
  isScreening,
  answers = [],
  answersWithLogicFromOutcomes = [],
}) {
  const { isEnabled, canSet } = canApplyLogicVisibility({
    nodeType,
    isScreening,
    answers,
    answersWithLogicFromOutcomes,
  });

  if (!isEnabled) {
    return answers;
  }

  const newAnswers = answers.map((answer) => {
    if (answer.isEmptyAnswer) {
      return answer;
    }

    const hasLogic = answersWithLogicFromOutcomes.includes(
      answer.answerRefId || answer.refId,
    );

    return {
      ...answer,
      isVisibleByLogicSet: hasLogic,
      isVisibleByLogicCannotSet:
        !canSet && !hasLogic && !answer.isCarriedForward,
    };
  });

  return newAnswers;
}

export function getDisplayLogicActionDisabled({
  quotaMode = QUOTA_MODE.NONE,
  randomizeQuestionnaireEnabled = false,
} = {}) {
  return quotaMode !== QUOTA_MODE.NONE || randomizeQuestionnaireEnabled;
}

export function isVisibilityActionButtonDisabled({
  hasPermission = true,
  isDisabled = false,
  cannotSet = false,
  inShuffledGroup = false,
  quotaMode = QUOTA_MODE.NONE,
  randomizeQuestionnaireEnabled = false,
} = {}) {
  const isDisplayLogicRestricted = getDisplayLogicActionDisabled({
    quotaMode,
    randomizeQuestionnaireEnabled,
  });

  return (
    !hasPermission ||
    isDisabled ||
    cannotSet ||
    inShuffledGroup ||
    isDisplayLogicRestricted
  );
}

export function getDisplayLogicSettingDisabledState({
  question,
  quotaMode = QUOTA_MODE.NONE,
  randomizeQuestionnaireEnabled = false,
  hasNavigationPermission = true,
  hasAnswerVisibilityPermission = true,
  hasSettingDisplayLogicPermission = true,
}) {
  const inShuffledGroup = Boolean(question?.hasParentNodeWithEnabledShuffling);
  const supportsAnswerDisplayLogic = canShowHideAnswers({
    type: question?.type,
    isScreening: question?.isScreening,
    inVanWestendorpGroup: question?.isInVanWestendorpGroup,
  });
  const effectiveHasAnswerVisibilityPermission =
    !supportsAnswerDisplayLogic || hasAnswerVisibilityPermission;

  const disabled =
    !hasNavigationPermission ||
    !hasSettingDisplayLogicPermission ||
    !effectiveHasAnswerVisibilityPermission ||
    isVisibilityActionButtonDisabled({
      hasPermission: true,
      inShuffledGroup,
      quotaMode,
      randomizeQuestionnaireEnabled,
    });

  if (!disabled) {
    return { disabled: false, tooltipMessage: null };
  }

  return {
    disabled: true,
    tooltipMessage: getTooltip({
      quotaMode,
      randomizeQuestionnaireEnabled,
    }),
  };
}

export function getTooltip({
  isSet = false,
  cannotSet = false,
  quotaMode = QUOTA_MODE.NONE,
  randomizeQuestionnaireEnabled = false,
} = {}) {
  if (randomizeQuestionnaireEnabled) {
    return questionnaireMessages.randomizeQuestionsEnabledAnswersLogic;
  }

  if (quotaMode === QUOTA_MODE.MAX) {
    return messages.cannotSetDueToQuotas;
  }

  if (quotaMode === QUOTA_MODE.MIN) {
    return messages.cannotSetDueToMinimumResponses;
  }

  if (cannotSet) {
    return messages.cannotSet;
  }

  if (isSet) {
    return messages.isSet;
  }

  return messages.isNotSet;
}

export function getTooltipValues(type) {
  if (type === ActionVisibilityValueType.Row) {
    return { type: VISIBILITY_ITEM_TYPE.ROW };
  }

  if (type === ActionVisibilityValueType.Column) {
    return { type: VISIBILITY_ITEM_TYPE.COLUMN };
  }

  return { type: VISIBILITY_ITEM_TYPE.ANSWER };
}

export function getErrorValues(errors) {
  if (errors.hasLogicVisibilityErrorOnRows) {
    return { type: VISIBILITY_ITEM_TYPE.ROW };
  }

  if (errors.hasLogicVisibilityErrorOnColumns) {
    return { type: VISIBILITY_ITEM_TYPE.COLUMN };
  }

  return { type: VISIBILITY_ITEM_TYPE.ANSWER };
}

export function canShowHideAnswerOnlyInRows(node) {
  return isMatrixBipolarOrConstantNode(node);
}

export function isNodeSupportConditionalDisplayLogicOnAnswers(nodeType) {
  return QUESTIONS_SUPPORTING_SHOW_HIDE_ANSWERS.includes(nodeType);
}

/**
 * Answers, matrix rows, and matrix/bipolar columns used for visibility.
 * Lists the question type does not have come back empty.
 */
export function getQuestionVisibilityOptions(node) {
  return {
    answers:
      getAnswersVisibilityList(node, ActionVisibilityValueType.Answer) || [],
    rows: getAnswersVisibilityList(node, ActionVisibilityValueType.Row) || [],
    columns:
      getAnswersVisibilityList(node, ActionVisibilityValueType.Column) || [],
  };
}

/**
 * Whether any of the question's answers, rows, or columns appear in `refIds`.
 * Matches on `refId` only.
 */
export function hasQuestionOptionInRefIds(node, refIds = []) {
  const { answers, rows, columns } = getQuestionVisibilityOptions(node);

  const optionRefIds = uniq(
    answers.concat(rows, columns).map((option) => option.refId),
  );

  return optionRefIds.some((optionRefId) => refIds.includes(optionRefId));
}

export function getAnswersVisibilityList(node, valueType) {
  if (
    valueType === ActionVisibilityValueType.Row &&
    !isMatrixSingleOrMultipleNode(node)
  ) {
    return node.matrix?.matrixAnswers;
  }

  if (valueType === ActionVisibilityValueType.Column) {
    if (isMatrixSingleOrMultipleNode(node)) {
      return node.scorepoints;
    }

    return node.matrix?.columns;
  }

  return node.answers;
}
